import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { cloneDeep } from 'lodash-es'
import { MAX_GROSS_ANNUAL_SALARY_LIMIT, MAX_SUPER_FUND_LIMIT } from '@/composables/useRetirementCalculator'
import { formatCurrency } from '@/lib/helpers'
import { useRetirementCalculatorStore } from './RetirementCalculatorStore'
import type { RetirementData, IncomeAsset, CareerBreak } from './RetirementCalculator/types'

interface ErrorTemplate {
  age: string
  income: string
  current_super_balance: string
  hasHome: string
  contribution_rate: string
  desired_retirement_income: string
  desired_retirement_age: string
  super_access_age: string
  property_income: string
  dividends_income: string
  cash_interest: string
  before_tax_contribution: string
  after_tax_contribution: string
  one_off_contribution: string
  one_off_age: string
  part_time_starting_age: string
  part_time_days: string
  part_time_duration: string
  career_breaks: Record<number, Record<string, string>>
}

const createEmptyErrorTemplate = (): ErrorTemplate => ({
  age: '',
  income: '',
  current_super_balance: '',
  hasHome: '',
  contribution_rate: '',
  desired_retirement_income: '',
  desired_retirement_age: '',
  super_access_age: '',
  property_income: '',
  dividends_income: '',
  cash_interest: '',
  before_tax_contribution: '',
  after_tax_contribution: '',
  one_off_contribution: '',
  one_off_age: '',
  part_time_starting_age: '',
  part_time_days: '',
  part_time_duration: '',
  career_breaks: {}
})

export const useRetirementCalculatorErrorStore = defineStore('RetirementCalculatorErrorStore', () => {
  // State
  const userErrors = ref<ErrorTemplate>(createEmptyErrorTemplate())
  const partnerErrors = ref<ErrorTemplate>(createEmptyErrorTemplate())
  const assetErrors = ref<Record<number, Record<string, string>>>({})
  const errorTabLocations = ref<number[]>([])

  // Getters
  const getUserErrors = computed(() => userErrors.value)
  const getPartnerErrors = computed(() => partnerErrors.value)
  const getUserBreakErrors = computed(() => userErrors.value.career_breaks)
  const getPartnerBreakErrors = computed(() => partnerErrors.value.career_breaks)
  const getAssetErrors = computed(() => assetErrors.value)
  const getErrorLocations = computed(() => errorTabLocations.value)

  // Actions
  const resetErrors = () => {
    userErrors.value = createEmptyErrorTemplate()
    partnerErrors.value = createEmptyErrorTemplate()
    assetErrors.value = {}
    errorTabLocations.value = []
  }

  const setErrorLocations = (value: number[]) => {
    errorTabLocations.value = value
  }

  const updateUserError = (payload: { field: keyof ErrorTemplate; value: string }) => {
    if (payload.field === 'career_breaks') return // Handle separately
    userErrors.value[payload.field] = payload.value as any
  }

  const updatePartnerError = (payload: { field: keyof ErrorTemplate; value: string }) => {
    if (payload.field === 'career_breaks') return // Handle separately
    partnerErrors.value[payload.field] = payload.value as any
  }

  const updateCareerBreakError = (payload: {
    owner: 'userErrors' | 'partnerErrors'
    index: number
    field: string
    value: string
  }) => {
    const target = payload.owner === 'userErrors' ? userErrors.value : partnerErrors.value
    if (!target.career_breaks[payload.index]) {
      target.career_breaks[payload.index] = {}
    }
    target.career_breaks[payload.index][payload.field] = payload.value
  }

  const removeCareerBreakError = (payload: { owner: 'userData' | 'partnerData'; index: number }) => {
    const target = payload.owner === 'userData' ? userErrors.value : partnerErrors.value
    if (target.career_breaks[payload.index]) {
      target.career_breaks[payload.index] = {}
    }
  }

  const updateAssetError = (payload: { index: number; field: string; value: string }) => {
    if (!assetErrors.value[payload.index]) {
      assetErrors.value[payload.index] = {}
    }
    assetErrors.value[payload.index][payload.field] = payload.value
  }

  const removeIncomeAssetError = (index: number) => {
    if (assetErrors.value[index]) {
      assetErrors.value[index] = {}
    }
  }

  const validateCareerBreaks = (payload: {
    careerBreaks: CareerBreak[]
    errorLocations: number[]
    owner: 'userErrors' | 'partnerErrors'
    valid: boolean
  }) => {
    const output = { errorLocations: [...payload.errorLocations], valid: payload.valid }
    
    payload.careerBreaks.forEach((cBreak, index) => {
      const bothNull = cBreak.age === null && cBreak.duration === null
      
      if (!bothNull && cBreak.age === null) {
        updateCareerBreakError({
          value: 'Please enter the age',
          field: 'age',
          index,
          owner: payload.owner
        })
        output.valid = false
        if (!payload.errorLocations.includes(3)) output.errorLocations.push(3)
      }
      
      if (!bothNull && cBreak.duration === null) {
        updateCareerBreakError({
          value: 'Please enter the duration',
          field: 'duration',
          index,
          owner: payload.owner
        })
        output.valid = false
        if (!payload.errorLocations.includes(3)) output.errorLocations.push(3)
      }
    })
    
    return output
  }

  const validateRetirementCalculatorFields = async (
    userData: RetirementData,
    partnerData: RetirementData,
    enablePartnerForm: boolean,
    incomeAssetArray: IncomeAsset[]
  ): Promise<boolean> => {
    const retCalcStore = useRetirementCalculatorStore()
    retCalcStore.setGraphLoading(true)
    
    let valid = true
    resetErrors()
    let errorLocations: number[] = []

    // User age validation
    if (userData.age === null || userData.age === 0) {
      updateUserError({ value: 'Please enter a valid age', field: 'age' })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }
    
    if (userData.age > userData.desired_retirement_age) {
      updateUserError({
        value: 'Your age cannot be more than your retirement age',
        field: 'age'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }

    // Partner age validation
    if (enablePartnerForm && partnerData.age > partnerData.desired_retirement_age) {
      updatePartnerError({
        value: 'Your partners age cannot be more than your retirement age',
        field: 'age'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }
    
    if (enablePartnerForm && (partnerData.age === null || partnerData.age === 0)) {
      updatePartnerError({ value: 'Please enter a valid age', field: 'age' })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }

    // User income validation
    if (userData.income === null || userData.income === 0) {
      updateUserError({ value: 'Please enter a valid income', field: 'income' })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }
    
    if (userData.income > MAX_GROSS_ANNUAL_SALARY_LIMIT) {
      updateUserError({
        value: `Maximum ${formatCurrency(MAX_GROSS_ANNUAL_SALARY_LIMIT)}`,
        field: 'income'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }

    // Home ownership validation
    if (userData.hasHome === null) {
      updateUserError({ value: 'Please provide an answer', field: 'hasHome' })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }

    // Partner income validation
    if (enablePartnerForm && (partnerData.income === null || partnerData.income === 0)) {
      updatePartnerError({ value: 'Please enter a valid income', field: 'income' })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }
    
    if (enablePartnerForm && partnerData.income > MAX_GROSS_ANNUAL_SALARY_LIMIT) {
      updatePartnerError({
        value: `Maximum ${formatCurrency(MAX_GROSS_ANNUAL_SALARY_LIMIT)}`,
        field: 'income'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }

    // User super balance validation
    if (userData.current_super_balance === null || userData.current_super_balance === 0) {
      updateUserError({
        value: 'Please enter a valid current super balance',
        field: 'current_super_balance'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }
    
    if (userData.current_super_balance > MAX_SUPER_FUND_LIMIT) {
      updateUserError({
        value: `Maximum ${formatCurrency(MAX_SUPER_FUND_LIMIT)}`,
        field: 'current_super_balance'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }

    // Partner super balance validation
    if (enablePartnerForm && (partnerData.current_super_balance === null || partnerData.current_super_balance === 0)) {
      updatePartnerError({
        value: 'Please enter a valid current super balance',
        field: 'current_super_balance'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }
    
    if (enablePartnerForm && partnerData.current_super_balance > MAX_SUPER_FUND_LIMIT) {
      updatePartnerError({
        value: `Maximum ${formatCurrency(MAX_SUPER_FUND_LIMIT)}`,
        field: 'current_super_balance'
      })
      if (!errorLocations.includes(0)) errorLocations.push(0)
      valid = false
    }

    // Retirement income validation
    if (userData.desired_retirement_income === null || userData.desired_retirement_income === 0) {
      updateUserError({
        value: 'Please enter a valid income',
        field: 'desired_retirement_income'
      })
      valid = false
      if (!errorLocations.includes(0)) errorLocations.push(0)
    }

    // Retirement age validation
    if (userData.desired_retirement_age === null || userData.desired_retirement_age === 0) {
      updateUserError({
        value: 'Please enter a valid age',
        field: 'desired_retirement_age'
      })
      valid = false
      if (!errorLocations.includes(0)) errorLocations.push(0)
    }
    
    if (enablePartnerForm && (partnerData.desired_retirement_age === null || partnerData.desired_retirement_age === 0)) {
      updatePartnerError({
        value: 'Please enter a valid age',
        field: 'desired_retirement_age'
      })
      valid = false
      if (!errorLocations.includes(0)) errorLocations.push(0)
    }

    // One-off contribution validation
    if (userData.one_off_age > 0 && userData.one_off_age < userData.age) {
      if (userData.age >= 75) {
        retCalcStore.updateUserDataKeyPair({ value: null, field: 'one_off_contribution' })
        retCalcStore.updateUserDataKeyPair({ value: null, field: 'one_off_age' })
      } else {
        updateUserError({
          value: 'Please enter an age older than your current age',
          field: 'one_off_age'
        })
        valid = false
        if (!errorLocations.includes(2)) errorLocations.push(2)
      }
    }
    
    if (enablePartnerForm && partnerData.one_off_age > 0 && partnerData.one_off_age < partnerData.age) {
      if (partnerData.age >= 75) {
        retCalcStore.updatePartnerDataKeyPair({ value: null, field: 'one_off_contribution' })
        retCalcStore.updatePartnerDataKeyPair({ value: null, field: 'one_off_age' })
      } else {
        updatePartnerError({
          value: 'Please enter an age older than your current age',
          field: 'one_off_age'
        })
        valid = false
        if (!errorLocations.includes(2)) errorLocations.push(2)
      }
    }

    // Before tax contribution validation
    if (userData.before_tax_contribution > 0 && userData.desired_retirement_age <= userData.age) {
      updateUserError({
        value: 'Contributions are accepted if not retired',
        field: 'before_tax_contribution'
      })
      valid = false
      if (!errorLocations.includes(2)) errorLocations.push(2)
    }
    
    if (enablePartnerForm && partnerData.before_tax_contribution > 0 && partnerData.desired_retirement_age <= partnerData.age) {
      updatePartnerError({
        value: 'Contributions are accepted if not retired',
        field: 'desired_retirement_age'
      })
      valid = false
      if (!errorLocations.includes(2)) errorLocations.push(2)
    }

    // After tax contribution age limits
    if (userData.after_tax_contribution > 0 && userData.age >= 75) {
      retCalcStore.updateUserDataKeyPair({ value: null, field: 'after_tax_contribution' })
    }
    if (partnerData.after_tax_contribution > 0 && partnerData.age >= 75) {
      retCalcStore.updatePartnerDataKeyPair({ value: null, field: 'after_tax_contribution' })
    }

    // One-off contribution completeness validation
    if (userData.one_off_contribution > 0 && !userData.one_off_age) {
      if (userData.age >= 75) {
        retCalcStore.updateUserDataKeyPair({ value: null, field: 'one_off_contribution' })
        retCalcStore.updateUserDataKeyPair({ value: null, field: 'one_off_age' })
      } else {
        updateUserError({
          value: 'Please enter an age and an amount',
          field: 'one_off_age'
        })
        valid = false
        if (!errorLocations.includes(2)) errorLocations.push(2)
      }
    }
    
    if (partnerData.one_off_contribution > 0 && !partnerData.one_off_age) {
      if (partnerData.age >= 75) {
        retCalcStore.updatePartnerDataKeyPair({ value: null, field: 'one_off_contribution' })
        retCalcStore.updatePartnerDataKeyPair({ value: null, field: 'one_off_age' })
      } else {
        updatePartnerError({
          value: 'Please enter an age and an amount',
          field: 'one_off_age'
        })
        valid = false
        if (!errorLocations.includes(2)) errorLocations.push(2)
      }
    }

    // Part-time work validation
    const userPartTime = userData.part_time
    const userAgeValueAbsent = userPartTime.part_time_starting_age === null || userPartTime.part_time_starting_age === 0
    const userDurationValueAbsent = userPartTime.part_time_duration === null || userPartTime.part_time_duration === 0
    const userPartTimeBothNull = userAgeValueAbsent && userDurationValueAbsent
    
    if (userPartTime.part_time_starting_age > 0 && userPartTime.part_time_starting_age < userData.age) {
      updateUserError({
        value: `Min: ${userData.age}`,
        field: 'part_time_starting_age'
      })
      valid = false
      if (!errorLocations.includes(3)) errorLocations.push(3)
    }
    
    if (!userPartTimeBothNull) {
      if (userAgeValueAbsent) {
        updateUserError({
          value: 'Please enter a starting age',
          field: 'part_time_starting_age'
        })
        valid = false
        if (!errorLocations.includes(3)) errorLocations.push(3)
      }
      if (userDurationValueAbsent) {
        updateUserError({
          value: 'Please enter the duration',
          field: 'part_time_duration'
        })
        valid = false
        if (!errorLocations.includes(3)) errorLocations.push(3)
      }
      if ((userPartTime.part_time_starting_age > 0 || userPartTime.part_time_duration > 0) && userPartTime.part_time_days === null) {
        updateUserError({
          value: "Enter days you'll work in a week",
          field: 'part_time_days'
        })
        valid = false
        if (!errorLocations.includes(3)) errorLocations.push(3)
      }
    }

    // Partner part-time work validation
    const partPartTime = partnerData.part_time
    const partnerAgeValueAbsent = partPartTime.part_time_starting_age === null || partPartTime.part_time_starting_age === 0
    const partnerDurationAbsent = partPartTime.part_time_duration === null || partPartTime.part_time_duration === 0
    const partPartTimeBothNull = partnerAgeValueAbsent && partnerDurationAbsent
    
    if (partPartTime.part_time_starting_age > 0 && partPartTime.part_time_starting_age < partnerData.age) {
      updatePartnerError({
        value: `Min: ${partnerData.age}`,
        field: 'part_time_starting_age'
      })
      valid = false
      if (!errorLocations.includes(3)) errorLocations.push(3)
    }
    
    if (!partPartTimeBothNull) {
      if (partnerAgeValueAbsent) {
        updatePartnerError({
          value: 'Please enter a starting age',
          field: 'part_time_starting_age'
        })
        valid = false
        if (!errorLocations.includes(3)) errorLocations.push(3)
      }
      if (partnerDurationAbsent) {
        updatePartnerError({
          value: 'Please enter the duration',
          field: 'part_time_duration'
        })
        valid = false
        if (!errorLocations.includes(3)) errorLocations.push(3)
      }
      if ((partPartTime.part_time_starting_age > 0 || partPartTime.part_time_duration > 0) && partPartTime.part_time_days === null) {
        updatePartnerError({
          value: "Enter days you'll work in a week",
          field: 'part_time_days'
        })
        valid = false
        if (!errorLocations.includes(3)) errorLocations.push(3)
      }
    }

    // Income asset validation
    incomeAssetArray.forEach((inAss, index) => {
      const bothAreNull = inAss.type === null && (inAss.current_value === null || inAss.current_value <= 0)
      
      if (!bothAreNull && inAss.type === null) {
        updateAssetError({ value: 'Please enter the type', field: 'type', index })
        valid = false
        if (!errorLocations.includes(1)) errorLocations.push(1)
      }
      
      if (!bothAreNull && (inAss.current_value === null || inAss.current_value <= 0)) {
        updateAssetError({
          value: 'Please enter the current value',
          field: 'current_value',
          index
        })
        valid = false
        if (!errorLocations.includes(1)) errorLocations.push(1)
      }
    })

    // Career breaks validation
    const careerBreakOutput = validateCareerBreaks({
      careerBreaks: userData.career_breaks,
      errorLocations: errorLocations,
      owner: 'userErrors',
      valid
    })
    errorLocations = careerBreakOutput.errorLocations
    valid = careerBreakOutput.valid

    if (enablePartnerForm) {
      const partnerCareerBreakOutput = validateCareerBreaks({
        careerBreaks: partnerData.career_breaks,
        errorLocations: errorLocations,
        owner: 'partnerErrors',
        valid
      })
      errorLocations = partnerCareerBreakOutput.errorLocations
      valid = partnerCareerBreakOutput.valid
    }

    if (!valid) setErrorLocations(errorLocations)
    return valid
  }

  return {
    // State
    userErrors,
    partnerErrors,
    assetErrors,
    errorTabLocations,

    // Getters
    getUserErrors,
    getPartnerErrors,
    getUserBreakErrors,
    getPartnerBreakErrors,
    getAssetErrors,
    getErrorLocations,

    // Actions
    resetErrors,
    setErrorLocations,
    updateUserError,
    updatePartnerError,
    updateCareerBreakError,
    removeCareerBreakError,
    updateAssetError,
    removeIncomeAssetError,
    validateCareerBreaks,
    validateRetirementCalculatorFields,
  }
})